import React, { Suspense, lazy, useMemo } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { LoginForm } from './components/LoginForm'
import { Layout } from './components/layout/Layout'
import { NavigationService } from './config/routes'
import type { UserRole } from './components/data/orderWorkflowData'
import { usePushNotifications } from './hooks/usePushNotifications'

// Retry dynamic import once on chunk load failure (stale deployment)
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((error: Error) => {
      // Only auto-reload once per session to avoid infinite loops
      const key = `chunk_reload_${  window.location.pathname}`
      const alreadyRetried = sessionStorage.getItem(key)
      if (!alreadyRetried) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
      }
      throw error
    })
  )
}

// Lazy load all pages for better performance
const OrdersPage = lazyWithRetry(() => import('./components/pages/OrdersPage').then(m => ({ default: m.OrdersPage })))
const BouquetsPage = lazyWithRetry(() => import('./components/pages/BouquetsPage').then(m => ({ default: m.BouquetsPage })))
const FlowersPage = lazyWithRetry(() => import('./components/pages/FlowersPage').then(m => ({ default: m.FlowersPage })))
const CategoriesPage = lazyWithRetry(() => import('./components/pages/CategoriesPage').then(m => ({ default: m.CategoriesPage })))
const ClientsPage = lazyWithRetry(() => import('./components/pages/ClientsPage').then(m => ({ default: m.ClientsPage })))
const CouriersPage = lazyWithRetry(() => import('./components/pages/CouriersPage').then(m => ({ default: m.CouriersPage })))
const FloristsPage = lazyWithRetry(() => import('./components/pages/FloristsPage').then(m => ({ default: m.FloristsPage })))
const SupportPage = lazyWithRetry(() => import('./components/pages/SupportPage').then(m => ({ default: m.SupportPage })))
const SettingsPage = lazyWithRetry(() => import('./components/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const FloristWorkspacePage = lazyWithRetry(() => import('./components/pages/FloristWorkspacePage').then(m => ({ default: m.FloristWorkspacePage })))
const CourierWorkspacePage = lazyWithRetry(() => import('./components/pages/CourierWorkspacePage').then(m => ({ default: m.CourierWorkspacePage })))
const OrderWorkflowPage = lazyWithRetry(() => import('./components/pages/OrderWorkflowPage').then(m => ({ default: m.OrderWorkflowPage })))
const UsersPage = lazyWithRetry(() => import('./components/pages/UsersPage').then(m => ({ default: m.UsersPage })))
const StoresPage = lazyWithRetry(() => import('./components/pages/StoresPage').then(m => ({ default: m.StoresPage })))
const RequirementsPage = lazyWithRetry(() => import('./components/pages/RequirementsPage').then(m => ({ default: m.RequirementsPage })))
const PromotionsPage = lazyWithRetry(() => import('./components/pages/PromotionsPage').then(m => ({ default: m.PromotionsPage })))
const HomeLayoutPage = lazyWithRetry(() => import('./components/pages/HomeLayoutPage').then(m => ({ default: m.HomeLayoutPage })))
const ShowcasesPage = lazyWithRetry(() => import('./components/pages/ShowcasesPage').then(m => ({ default: m.ShowcasesPage })))
const FeaturedTilesPage = lazyWithRetry(() => import('./components/pages/FeaturedTilesPage').then(m => ({ default: m.FeaturedTilesPage })))
const DeliverySettingsPage = lazyWithRetry(() => import('./components/pages/DeliverySettingsPage'))
const AISettingsPage = lazyWithRetry(() => import('./components/pages/AISettingsPage').then(m => ({ default: m.AISettingsPage })))
const BehaviorAnalyticsPage = lazyWithRetry(() => import('./components/pages/BehaviorAnalyticsPage').then(m => ({ default: m.BehaviorAnalyticsPage })))
const DashboardPage = lazyWithRetry(() => import('./components/pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const PaymentsReportPage = lazyWithRetry(() => import('./components/pages/PaymentsReportPage').then(m => ({ default: m.PaymentsReportPage })))

import './styles/globals.css'

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-2">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <div className="text-sm text-muted-foreground">Səhifə yüklənir...</div>
    </div>
  </div>
)

const NotAuthorized: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center px-4">
    <div className="max-w-sm text-center space-y-2">
      <h2 className="text-xl font-semibold">Giriş qadağandır</h2>
      <p className="text-sm text-muted-foreground">
        Bu səhifəni görmək üçün icazəniz yoxdur. Zəhmət olmasa başqa bölmə seçin və ya administratora müraciət edin.
      </p>
    </div>
  </div>
)

const RequireRole: React.FC<{
  allowedRoles?: UserRole[] | undefined
  userRole: UserRole
  children: React.ReactElement
}> = ({ allowedRoles, userRole, children }) => {
  if (!allowedRoles || allowedRoles.includes(userRole)) {
    return children
  }

  return <NotAuthorized />
}

const ProtectedRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth()

  // ✅ PHASE 4: Initialize push notifications (auto-requests for couriers)
  usePushNotifications()

  const routePermissions = useMemo(() => {
    const map = new Map<string, UserRole[] | undefined>()

    // Build permissions map from NavigationService
    const allRoutes = NavigationService.getRoutesForRole('admin') // Get all routes (admin sees everything)
    allRoutes.forEach(route => {
      map.set(route.path, route.allowedRoles)
    })

    // Add workspace routes (not in main ROUTES list)
    map.set('/florist-workspace', ['admin', 'florist'])
    map.set('/courier-workspace', ['admin', 'courier'])

    return map
  }, [])
  const getAllowedRoles = (path: string) => routePermissions.get(path) ?? undefined

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Yüklənir...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginForm />
  }

  const userRole = (user?.frontendRole ?? 'customer') as UserRole
  const userId = user?.id ?? 'system'

  const floristWorkspaceProps = user?.id ? { floristId: user.id } : {}
  const courierWorkspaceProps = user?.id ? { courierId: user.id } : {}

  // ✅ PHASE 4: Role-based default landing page
  const getDefaultLandingPage = (role: UserRole): string => {
    switch (role) {
      case 'courier':
        return '/courier-workspace'
      case 'florist':
        return '/florist-workspace'
      case 'admin':
        return '/dashboard'
      default:
        return '/orders'
    }
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route
          index
          element={<Navigate to={getDefaultLandingPage(userRole)} replace />}
        />
        <Route
          path="dashboard"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/dashboard')}>
              <Suspense fallback={<PageLoader />}>
                <DashboardPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="order-workflow"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/order-workflow')}>
              <Suspense fallback={<PageLoader />}>
                <OrderWorkflowPage userRole={userRole} userId={userId} />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="orders"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/orders')}>
              <Suspense fallback={<PageLoader />}>
                <OrdersPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="flowers"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/flowers')}>
              <Suspense fallback={<PageLoader />}>
                <FlowersPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="bouquets"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/bouquets')}>
              <Suspense fallback={<PageLoader />}>
                <BouquetsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="categories"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/categories')}>
              <Suspense fallback={<PageLoader />}>
                <CategoriesPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="requirements"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/requirements')}>
              <Suspense fallback={<PageLoader />}>
                <RequirementsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="florists"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/florists')}>
              <Suspense fallback={<PageLoader />}>
                <FloristsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="clients"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/clients')}>
              <Suspense fallback={<PageLoader />}>
                <ClientsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="users"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/users')}>
              <Suspense fallback={<PageLoader />}>
                <UsersPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="stores"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/stores')}>
              <Suspense fallback={<PageLoader />}>
                <StoresPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="support"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/support')}>
              <Suspense fallback={<PageLoader />}>
                <SupportPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="couriers"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/couriers')}>
              <Suspense fallback={<PageLoader />}>
                <CouriersPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="promotions"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/promotions')}>
              <Suspense fallback={<PageLoader />}>
                <PromotionsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="home-layout"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/home-layout')}>
              <Suspense fallback={<PageLoader />}>
                <HomeLayoutPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="showcases"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/showcases')}>
              <Suspense fallback={<PageLoader />}>
                <ShowcasesPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="features"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/features')}>
              <Suspense fallback={<PageLoader />}>
                <FeaturedTilesPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="delivery-settings"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/delivery-settings')}>
              <Suspense fallback={<PageLoader />}>
                <DeliverySettingsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="ai-settings"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/ai-settings')}>
              <Suspense fallback={<PageLoader />}>
                <AISettingsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="settings"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/settings')}>
              <Suspense fallback={<PageLoader />}>
                <SettingsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="behavior-analytics"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/behavior-analytics')}>
              <Suspense fallback={<PageLoader />}>
                <BehaviorAnalyticsPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="payments-report"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/payments-report')}>
              <Suspense fallback={<PageLoader />}>
                <PaymentsReportPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="florist-workspace"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/florist-workspace')}>
              <Suspense fallback={<PageLoader />}>
                <FloristWorkspacePage {...floristWorkspaceProps} />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="courier-workspace"
          element={
            <RequireRole userRole={userRole} allowedRoles={getAllowedRoles('/courier-workspace')}>
              <Suspense fallback={<PageLoader />}>
                <CourierWorkspacePage {...courierWorkspaceProps} />
              </Suspense>
            </RequireRole>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

const App: React.FC = () => {
  return <ProtectedRoutes />
}

export default App

